import type { RecurrenceAnchorValue } from '../dto/deadline/deadline.constants';
import {
  DEADLINE_AI_CONFIDENCE_THRESHOLD,
  type DeadlineAiDueAtSource,
  type DeadlineAiProposal,
  type DeadlineAiReading,
  type DeadlineAiSuggestionDto,
  type DeadlineAiTypeTerms,
  type LinkedDeadlineTerms,
} from '../dto/deadline/deadline-ai.constants';
import type { DeadlineRecurrenceChoice } from '../dto/deadline/deadline-management.constants';
import { isOtherDocumentType } from '../dto/document-type/document-type-catalog.constants';
import { computeRenewedDueDate } from './deadline-dates';
import { resolveRecurrencePreselection } from './deadline-recurrence';

export function readConfidentValue(
  reading: DeadlineAiReading,
  threshold: number = DEADLINE_AI_CONFIDENCE_THRESHOLD,
): string | null {
  return reading.value !== null && reading.confidence >= threshold ? reading.value : null;
}

/**
 * Preselezione per un documento non ancora collegato: wrapper sottile della regola del dialog §11.1
 * (`resolveRecurrencePreselection`, tranche 9: NONE solo senza ancora, senza preavvisi e mai per "Altro")
 * con l'unico fatto che solo la lettura conosce: una scadenza stampata, quando il tipo non è
 * ricorrente, propone ONE_OFF.
 */
export function suggestRecurrenceForUnlinkedDocument(input: {
  type: DeadlineAiTypeTerms | null;
  expiresAt: string | null;
}): DeadlineRecurrenceChoice | null {
  const preselected = resolveRecurrencePreselection(input.type);
  if (preselected === 'RECURRING') {
    return 'RECURRING';
  }
  if (input.expiresAt !== null) {
    return 'ONE_OFF';
  }
  return preselected ?? null;
}

export interface SuggestedDueAtInput {
  recurrence: DeadlineRecurrenceChoice | null;
  issuedAt: string | null;
  validityMonths: number | null;
  anchor: RecurrenceAnchorValue | null;
  previousDueAt: string | null;
  expiresAt: string | null;
}

export interface SuggestedDueAt {
  dueAt: string | null;
  dueAtSource: DeadlineAiDueAtSource | null;
}

const NO_DUE_DATE: SuggestedDueAt = { dueAt: null, dueAtSource: null };

/** Ricorrente: calcolata, mai letta. Unica: letta. Nessuna scadenza: nulla. */
export function computeSuggestedDueAt(input: SuggestedDueAtInput): SuggestedDueAt {
  if (input.recurrence === 'RECURRING') {
    if (input.issuedAt === null || input.validityMonths === null) {
      return NO_DUE_DATE;
    }
    return {
      dueAt: computeRenewedDueDate({
        anchor: input.anchor ?? 'FROM_ISSUE_DATE',
        issuedAt: input.issuedAt,
        previousDueAt: input.previousDueAt,
        validityMonths: input.validityMonths,
      }),
      dueAtSource: 'COMPUTED',
    };
  }
  if (input.recurrence === 'ONE_OFF' && input.expiresAt !== null) {
    return { dueAt: input.expiresAt, dueAtSource: 'READ' };
  }
  return NO_DUE_DATE;
}

export interface BuildDeadlineAiSuggestionInput {
  proposal: DeadlineAiProposal;
  /** Tipo letto dall'IA, già filtrato dalla soglia e risolto sul catalogo. */
  aiType: DeadlineAiTypeTerms | null;
  /** Tipo attuale del `SignedDocument` (scelto all'upload). */
  documentType: DeadlineAiTypeTerms | null;
  linked: LinkedDeadlineTerms | null;
}

function mismatchName(
  chosenTypeId: string | null,
  aiType: DeadlineAiTypeTerms | null,
): string | null {
  return chosenTypeId !== null && aiType !== null && aiType.id !== chosenTypeId
    ? aiType.name
    : null;
}

export function buildDeadlineAiSuggestion(
  input: BuildDeadlineAiSuggestionInput,
): DeadlineAiSuggestionDto {
  const issuedAt = readConfidentValue(input.proposal.issuedAt);
  const expiresAt = readConfidentValue(input.proposal.expiresAt);
  if (input.linked !== null) {
    const { linked } = input;
    return {
      documentTypeId: linked.documentTypeId,
      typeSource: linked.documentTypeId === null ? null : 'DEADLINE',
      typeMismatchName: mismatchName(linked.documentTypeId, input.aiType),
      recurrence: linked.recurrence,
      issuedAt,
      validityMonths: linked.validityMonths,
      recurrenceAnchor: linked.recurrenceAnchor,
      previousDueAt: linked.previousDueAt,
      ...computeSuggestedDueAt({
        recurrence: linked.recurrence,
        issuedAt,
        validityMonths: linked.validityMonths,
        anchor: linked.recurrenceAnchor,
        previousDueAt: linked.previousDueAt,
        expiresAt,
      }),
    };
  }
  const userType =
    input.documentType && !isOtherDocumentType(input.documentType) ? input.documentType : null;
  const chosenType = userType ?? input.aiType;
  const recurrence = suggestRecurrenceForUnlinkedDocument({ type: chosenType, expiresAt });
  const validityMonths = chosenType?.defaultValidityMonths ?? null;
  const recurrenceAnchor = chosenType?.recurrenceAnchor ?? null;
  return {
    documentTypeId: chosenType?.id ?? null,
    typeSource: userType ? 'DOCUMENT' : chosenType ? 'AI' : null,
    typeMismatchName: userType ? mismatchName(userType.id, input.aiType) : null,
    recurrence,
    issuedAt,
    validityMonths,
    recurrenceAnchor,
    previousDueAt: null,
    ...computeSuggestedDueAt({
      recurrence,
      issuedAt,
      validityMonths,
      anchor: recurrenceAnchor,
      previousDueAt: null,
      expiresAt,
    }),
  };
}
